import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap text-body1 font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", {
    variants: {
        variant: {
            // border-[2px]: focus hairline (allowlisted)
            default: "rounded-full bg-primary-500 text-neutral-white hover:bg-primary-600 disabled:bg-neutral-border-300 disabled:text-neutral-text-400 focus:border-state-warning focus:border-[2px]",
            secondary: "rounded-full bg-neutral-border-300 text-neutral-text-800 hover:bg-neutral-text-400 hover:text-neutral-text-900 disabled:bg-neutral-border-300 disabled:text-neutral-text-400 focus:border-state-warning focus:border-[2px]",
            ghost: "hover:bg-neutral-background-100",
        },
        size: {
            md: "h-11 p-3",
            sm: "h-8 text-body2 font-medium gap-1.5 px-3 has-[>svg]:px-2.5",
            lg: "h-12 px-6 text-body1 has-[>svg]:px-4",
            xs: "h-auto text-body2 font-medium gap-1.5 p-0 hover:bg-transparent",
            icon: "h-11 w-11 rounded-m p-0 has-[>svg]:p-0",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "md",
    },
});

function Button({
    className,
    variant,
    size,
    asChild = false,
    loading = false,
    disabled,
    children,
    ...props
}: React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
        loading?: boolean;
    }) {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;
    return (
        <Comp
            data-slot="button"
            data-loading={loading ? "true" : undefined}
            className={cn(buttonVariants({ variant, size, className }), "cursor-pointer")}
            disabled={isDisabled}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <Slottable>{children}</Slottable>
        </Comp>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
